import * as React from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Button, Breadcrumb } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

interface IAutomationBuilderPageProps {
  baseUri: string;
}

interface RouteParams {
  offerId: string;
}

export const AutomationBuilderPage: React.FC<IAutomationBuilderPageProps> = ({
  baseUri,
}) => {
  const { offerId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const automationType = searchParams.get('type') || 'unknown';

  const handleBackClick = () => {
    history.push(`${baseUri}/offers/${offerId}/settings`);
  };

  const getAutomationTitle = (type: string) => {
    switch (type) {
      case 'welcome-email':
        return 'Welcome Email Automation';
      case 'tier-unlocked':
        return 'Tier Unlocked Automation';
      case 'x-conversions-away-from-next-tier':
        return 'Conversions Away From Next Tier';
      case 'x-sales-amount-away-from-next-tier':
        return 'Sales Amount Away From Next Tier';
      case 'reward-unlocked':
        return 'Reward Unlocked Automation';
      case 'x-sales-amount-away-from-reward':
        return 'Sales Amount Away From Reward';
      case 'x-conversions-away-from-reward':
        return 'Conversions Away From Reward';
      default:
        return 'Create Automation';
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header with Breadcrumb and Navigation */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          <div className="px-6 py-4">
            {/* Breadcrumb */}
            <div className="mb-4">
              <Breadcrumb
                className="text-sm"
              />
            </div>

            {/* Header with Back Button */}
            <div className="flex items-center gap-4">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleBackClick}
                type="text"
                size="large"
                className="flex items-center justify-center w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-50"
              />
              <h1 className="text-2xl font-semibold text-gray-900">
                {getAutomationTitle(automationType)}
              </h1>
              <Button type="link" className="ml-auto text-blue-600 hover:text-blue-700">
                Need Help?
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <Card className="bg-white border border-gray-200">
            <CardContent className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {getAutomationTitle(automationType)}
                </h2>
                <p className="text-sm text-gray-600">
                  Configure your automation settings and workflow below.
                </p>
              </div>

              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Automation Builder</h3>
                <p className="text-gray-600">
                  The automation builder for "{getAutomationTitle(automationType)}" is coming soon.
                </p>
                <div className="mt-6">
                  <p className="text-sm text-gray-500">
                    Automation Type: <span className="font-medium">{automationType}</span>
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <Button onClick={handleBackClick}>
                  Cancel
                </Button>
                <Button type="primary" className="bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700">
                  Save Automation
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AutomationBuilderPage;
